@use '~/vendor/cxy/scss/styles/index.scss' as cxy;

$containerName: tableComponent;
$color-background: var(--cxy-color-surface-success-default-subtle-hover);
$color-heading: var(--cxy-color-surface-primary-default-subtle);
$color-dark: var(--cxy-color-surface-primary-default-subtle);
$color-highlight: color-mix(in srgb, var(--cxy-color-surface-page), $color-background 90%);

.tableGroup {
  border-radius: var(--cxy-border-radius-m);
  overflow: hidden;
  box-shadow: var(--cxy-box-shadow-c);

  .table {
    border-radius: 0;
    box-shadow: none;
  }
}

.table {
  display: table;
}

.tableLabel {
  @include cxy.font-size('text-xs');
  color: var(--cxy-color-text-default-body);
  margin: 0;
  background-color: $color-heading;
  padding-block: var(--cxy-spacing-xxsm);
  padding-inline: var(--cxy-spacing-xsm);
  text-align: start;

  p {
    margin: 0;
    @include cxy.font-size('text-xs');
  }
}

.tableWrapper {
  container-name: $containerName;
  container-type: inline-size;
}

.table {
  border-collapse: collapse;
  color: var(--cxy-color-text-default-body);
  width: 100%;
  background-color: $color-background;
  border-radius: var(--cxy-border-radius-m);
  box-shadow: var(--cxy-box-shadow-c);
  overflow: hidden;

  & + .table {
    border-block-start: var(--cxy-color-surface-default) 1px solid;
  }
}

.tableCaption {
  @include cxy.font-size('text-xs');
  background-color: $color-heading;
  padding-block: var(--cxy-spacing-3);
  padding-inline: var(--cxy-spacing-xsm);
  text-align: start;

  &,
  p {
    margin: 0;
    @include cxy.font-size('text-xs');
  }
}

.tableRow {
  min-height: 100%;
}

.tableHeader {
  .tableRow-size--s {
    > .tableColumn {
      font-size: var(--cxy-fontSize-xxs);
      font-weight: var(--cxy-fontWeight-medium);
    }
  }

  .tableRow-size--m {
    > .tableColumn {
      @include cxy.font-size('text-xs');
    }
  }
}

.tableBody,
.tableFooter {
  .tableRow-size--s {
    > .tableCell,
    > .tableColumn {
      font-size: var(--cxy-fontSize-xxs);
      font-weight: var(--cxy-fontWeight-semiBold);
    }
  }

  .tableRow-size--m {
    > .tableCell,
    > .tableColumn {
      @include cxy.font-size('text-s', var(--cxy-fontWeight-semiBold));
    }
  }
}

.tableHeader {
  .tableRow {
    .tableColumn {
      background-color: $color-heading;

      .headingText--mobile {
        + * {
          display: none;
        }
      }

      &.highlight {
        background-color: $color-highlight;
      }
    }
  }

  &[data-state='closed'] {
    .tableColumn {
      background-color: $color-dark !important;
    }
  }

  &[data-state='open'] {
    .tableColumn {
      background-color: $color-background !important;
    }
  }
}

.tableBody {
  .tableRow {
    row-gap: var(--cxy-spacing-xxsm);
  }
}

.tableFooter {
  background-color: $color-dark;
  padding-block: var(--cxy-spacing-3) var(--cxy-spacing-xsm);

  .tableRow .tableCell {
    margin-block-end: 0;
  }
}

.tableCell {
  padding-block: var(--cxy-spacing-xxsm);

  &.highlight {
    position: relative;
    text-align: center;
    background-color: $color-highlight;
    border-block-end-width: 6px;
    border-block-end-style: solid;
    border-block-end-color: var(--highlight);
  }
}

.showDividers + .showDividers {
  border-inline-start: var(--cxy-border-default);
}

.tableColumn,
.tableCell {
  padding-inline: var(--cxy-spacing-xsm);
  padding-block: var(--cxy-spacing-0);
  height: inherit;

  &:last-of-type {
    padding-block: var(--cxy-spacing-3);
  }

  &.highlight {
    background-color: $color-highlight;
    margin-inline-end: var(--cxy-spacing-xsm);
  }

  &.highlight--green {
    --highlight: var(--cxy-color-brand);
  }

  &.highlight--amber {
    --highlight: var(--cxy-color-container-warning);
  }

  &.highlight--red {
    --highlight: var(--cxy-color-container-danger);
  }

  &.mobile-hide {
    display: none;
  }
}

.cellWrapper,
.columnWrapper {
  &,
  .headingText,
  .headingText--mobile {
    display: flex;
    height: 100%;
    gap: var(--cxy-spacing-xxxsm);
    align-items: var(--alignItems);
    justify-content: var(--justifyContent);
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
}

.cellWrapper {
  flex-direction: column;
}

@include cxy.container-size($containerName, 'tablet-wide') {
  .tableHeader .tableRow {
    .tableColumn {
      &.showDividers {
        border-inline-start-color: transparent;
      }

      &.highlight {
        background-color: $color-heading;
      }
    }
  }

  .tableCell {
    &.showDividers {
      margin-block: var(--cxy-spacing-xxsm);
    }

    &.highlight {
      &:after {
        bottom: 0;
        margin-block-end: 0;
      }
    }

    &:not(.highlight) {
      margin-block-end: var(--cxy-spacing-xxsm);
    }
  }

  .tableColumn,
  .tableCell {
    padding-block: var(--cxy-spacing-3);

    &.highlight {
      margin-inline-end: 0;
    }

    &.mobile-hide {
      display: block;
    }
  }
}

@include cxy.container-size($containerName, 'tablet-wide') {
  .tableHeader {
    .tableRow {
      .tableColumn {
        .headingText--mobile {
          display: none;

          + * {
            display: flex;
          }
        }
      }
    }
  }
}

/* Gazzetta theme overrides */
[data-fpsm-theme='gazzetta'] {
  .tableCell.highlight {
    background-color: var(--cxy-color-surface-default) !important;

    &--green {
      --highlight: var(--cxy-color-success);
    }
    &--amber {
      --highlight: var(--cxy-color-content-warning-soft);
    }
  }
}
